import { authenticatedApiClient } from '../axios'
import dayjs from 'dayjs'

export interface EventFeedback {
  uuid: string
  generalRating?: number
  feedback?: string
  completed: boolean
  expiration: string
  user: string
  groupEvent?: {
    uuid: string
    title: string
    startTime: string
  }
  post?: {
    uuid: string
    title: string
    startTime: string
  }
  interestedDate?: string
  isCreator: boolean
}

export interface UpdateEventFeedbackRequest {
  uuid: string
  generalRating: number
  feedbackInputText: string
  type: 'groupEvent' | 'post'
}

class EventFeedbackService {
  async getPendingFeedback(userUuid: string): Promise<EventFeedback | null> {
    try {
      const where = {
        user: userUuid,
        completed: false,
        expiration: { '>': dayjs().toDate() },
      }

      const data = await authenticatedApiClient.get<EventFeedback[]>(`/groupeventfeedback`, {
        params: {
          where: JSON.stringify(where),
          limit: 1,
        },
      })
      return Array.isArray(data) && data.length > 0 ? data[0] : null
    } catch (error: any) {
      if (error?.response?.status === 404) return null
      throw this.handleError(error, 'Failed to fetch pending feedback', 'getPendingFeedback')
    }
  }

  async updateEventFeedback(request: UpdateEventFeedbackRequest): Promise<EventFeedback> {
    try {
      const urlPath = `${request.type === 'groupEvent' ? 'groupevent' : 'event'}feedback`
      const data = await authenticatedApiClient.patch<EventFeedback>(`/${urlPath}/${request.uuid}`, {
        generalRating: request.generalRating,
        completed: true,
        feedback: request.feedbackInputText,
      })
      return data
    } catch (error: any) {
      throw this.handleError(error, 'Failed to update event feedback', 'updateEventFeedback')
    }
  }

  async updateLastReviewedAt(timestamp: string, userUuid: string): Promise<any> {
    try {
      const data = await authenticatedApiClient.patch(`/user/${userUuid}`, {
        lastReviewedAt: timestamp,
      })
      return data
    } catch (error: any) {
      throw this.handleError(error, 'Failed to update last reviewed timestamp', 'updateLastReviewedAt')
    }
  }

  private handleError(error: any, message: string, operation: string): Error {
    // eslint-disable-next-line no-console
    console.error(`EventFeedbackService.${operation}:`, error)
    const msg = error?.response?.data?.message || error?.message || message
    return new Error(msg)
  }
}

const eventFeedbackService = new EventFeedbackService()
export default eventFeedbackService
export type { EventFeedbackService }

